import client from 'services/httpClient/commonClient'
import { all, put, select, delay, call } from 'redux-saga/effects'
import { takeEvery, takeLatest } from 'utils/effects/effects'
import { stopSubmit, reset } from 'redux-form'
import { LOCATION_CHANGE, push } from 'react-router-redux'
import geoCode from 'utils/geoCode/geoCode'

import {
  fromTrades,
  fromRouting,
  fromPro,
  fromContext,
  fromAuth,
} from 'store/selectors'
import pushGtmEvent from 'utils/gtm'
import getFormErrors from 'utils/formErrors'
import parseLocationSearch from 'utils/parseLocationSearch/parseLocationSearch'
import { saveSpnInCookie } from 'utils/spn/spn'
import {
  saveTokens,
  removeTokens,
  bootstrapAuthFromLs,
} from 'utils/token/token'
import wait from 'utils/wait'
import handleRequest from 'sagas/handleRequest/handleRequest'
import notify from 'sagas/notify/notify'
import { URLS } from 'constants/urls'
import {
  PRO_REGISTRATION_STEP_ONE,
  PRO_SIGNUP_QP_API_FORM_MAPPING,
  PRO_SIGNUP_API_FORM_MAPPING,
  PRO_REGISTRATION_FORM_NAME,
  PREFIX_CATEGORIES,
  NEW_FIRM_USER,
  CONFIRMATION_PATHS_FROM_PREFIX,
} from 'constants/pro'
import { PRO_API } from 'constants/services'

import {
  setProRoles,
  setProLoggedIn,
  closeAll,
  setRedirectPathname,
  proCreate,
  proAuthLogin,
  proSetSpn,
  firmDetails,
  setProFirmId,
  setNickname,
  setEmail,
  proForgotPassword,
  proResetPassword,
  proRegistration,
  setProFormRegistrationStep,
  registrationNumberData,
  setRegistrationNumberData,
  setPathNameOrigin,
  getPrefilledRegistration,
  CLIENT_INITIATED,
  PRO_CREATE,
  PRO_AUTH_LOGIN,
  PRO_AUTH_LOGOUT,
  PRO_UPDATE_PASSWORD,
  PRO_FORGOT_PASSWORD,
  PRO_REGISTRATION,
  REGISTRATION_NUMBER_DATA,
  GET_PREFILLED_REGISTRATION,
} from 'store/actions'
import { getFreeniumSignUpConfirmationPath } from 'routes'

import { requireTrades } from '../trades/sagas'
import { ACCESS_TOKEN, ID_TOKEN, REFRESH_TOKEN } from 'constants/auth'
import { ROLES } from 'constants/roles'
import jwtUtils from 'jwt-simple'
import IssuesTrackersService from 'services/issueTracker/issueTrackerService'

const urlPrefix = '/api'

const fromAPI = (apiViolations, apiFormMappings) =>
  apiViolations.map(apiViolation => {
    const { propertyPath: apiPath, message: apiMessage } = apiViolation
    const apiField = apiPath.replace(/errors\.(.*)/, '$1')
    const field = apiFormMappings.find(f => f.apiField === apiField).formField
    const message = apiMessage
      .replace(apiField, field)
      .replace('.pro.', '.user.')
    return {
      propertyPath: field,
      message,
    }
  })

const fromAPIV2 = (errorMessage, apiFormMappings) => {
  const violations = errorMessage.split('\n').filter(line => line.trim())

  return violations.map(violation => {
    const [propertyPath, message] = violation.split(':').map(s => s.trim())
    const apiField = propertyPath.split('.').pop()

    const mapping = apiFormMappings.find(f => f.apiField === apiField)

    if (!mapping) {
      console.warn(`No mapping found for field: ${apiField}`)
      return {
        propertyPath: apiField,
        message,
      }
    }

    const field = mapping.formField
    const formattedMessage = message
      .replace(apiField, field)
      .replace('.pro.', '.user.')

    return {
      propertyPath: field,
      message: formattedMessage,
    }
  })
}

const toAPI = (data, apiFormMappings) =>
  apiFormMappings.reduce(
    (payload, { apiField, formField }) => ({
      ...payload,
      [apiField]: data[formField],
    }),
    {},
  )

function* handleProCreateRequest({ data }) {
  try {
    yield* requireTrades()
    const trade = yield select(fromTrades.getTradeBySeoFriendlyName, data.trade)
    const payload = toAPI(
      { ...data, trade: trade.name },
      PRO_SIGNUP_QP_API_FORM_MAPPING,
    )
    const spn = yield select(fromPro.getSpn)
    if (spn) {
      payload.spn = spn
    }
    const response = yield* handleRequest({
      requestActions: proCreate,
      promise: call(client(PRO_API, true).post, `${urlPrefix}/pros`, payload),
    })
    if (response.data.url) {
      window.location.href = response.data.url
    }
  } catch (e) {
    if (e.response && e.response.status === 400) {
      yield put(
        stopSubmit(
          'SignUpQProForm',
          getFormErrors(
            fromAPI(e.response.data.violations, PRO_SIGNUP_QP_API_FORM_MAPPING),
          ),
        ),
      )
    } else {
      throw e
    }
  }
}

function* handleProLoginRequest({
  formName,
  email,
  password,
  redirectPathname,
}) {
  try {
    const pathName = yield select(fromRouting.getPathname)

    const response = yield* handleRequest({
      requestActions: proAuthLogin,
      promise: call(client(PRO_API, true).post, '/api/login', {
        username: email,
        password,
      }),
      checkTokens: false,
    })

    if (!response.data?.id_token || !response.data?.access_token) {
      yield put(stopSubmit(formName, { _error: 'user.sign_in.unknown_error' }))

      return
    }

    const jwt = jwtUtils.decode(response.data.id_token, null, true)
    if (!jwt) {
      yield put(stopSubmit(formName, { _error: 'user.sign_in.unknown_error' }))

      return
    }

    const roles = jwt[URLS.QUOTATIS.USER_AUTHORIZATION]
      ? jwt[URLS.QUOTATIS.USER_AUTHORIZATION].roles ?? []
      : jwt.custom_fields?.roles?.split(' ') ?? []

    if (roles.indexOf(ROLES.ROLE_FIRM_USER) !== -1) {
      yield put(
        stopSubmit(formName, { _error: 'user.sign_in.unauthorized_firm_user' }),
      )

      return
    }

    yield saveTokens({
      [ACCESS_TOKEN]: response.data.access_token,
      [ID_TOKEN]: response.data.id_token,
      [REFRESH_TOKEN]: response.data.refresh_token,
    })

    yield put(closeAll())
    yield put(reset(formName))
    yield put(setProLoggedIn(true))
    yield* notify('', 'user.sign_in.success')

    const decodedToken = yield select(fromAuth.selectIdTokenDecoded)
    yield decodeJwtToken(decodedToken, true)
    yield put(setPathNameOrigin(pathName))

    if (!redirectPathname) {
      redirectPathname = URLS.ROUTES.PRO_DASHBOARD
    }
    if (
      redirectPathname.startsWith('/espace-pro/') ||
      redirectPathname.startsWith('/espacio-pro/')
    ) {
      window.location.href = redirectPathname
    } else {
      yield put(push(redirectPathname))
    }
    yield put(setRedirectPathname(null))
  } catch (e) {
    IssuesTrackersService.captureError(new Error('Login failure'), {
      extra: {
        cause: e,
        loadBalancerRequestId: e.response?.headers['load-balancer-request-id'],
      },
    })
    yield put(
      stopSubmit(formName, { _error: `user.sign_in.${e.response.data.error}` }),
    )
  }
}

function* decodeJwtToken(decodedToken, isLoginRequest = false) {
  if (decodedToken) {
    let firmId
    let nickname
    let email = decodedToken.email
    let roles
    const appMetadata = decodedToken[URLS.QUOTATIS.APP_METADATA]
    if (appMetadata) {
      firmId = appMetadata?.impersonate?.firm ?? appMetadata.firm
      nickname = decodedToken.nickname
      email = appMetadata?.impersonate?.email ?? decodedToken.email
      roles = decodedToken[URLS.QUOTATIS.USER_AUTHORIZATION]?.roles
    } else if (decodedToken.custom_fields) {
      firmId = `/api/firms/${decodedToken.custom_fields.pro_id}`
      nickname = decodedToken.given_name
      roles = decodedToken.custom_fields.roles?.split(' ') ?? []
    }

    if (firmId) {
      yield put(firmDetails.request({ id: firmId, isLoginRequest }))
      yield put(setProFirmId(firmId))
    }
    if (nickname) {
      yield put(setNickname(nickname))
    }
    if (email) {
      yield put(setEmail(email))
    }
    if (roles) {
      yield put(setProRoles(roles))
    }
  }
}

function* handleProAuthLogout() {
  yield put(closeAll())
  yield put(setProLoggedIn(false))
  yield put(setProFirmId(''))
  yield put(setNickname(''))
  yield put(setEmail(''))
  removeTokens()

  yield put(push('/'))
  yield put(setRedirectPathname(null))
}

function* handleClientInitiated() {
  const queryParams = parseLocationSearch(window.location.search)
  if ('spn' in queryParams) {
    const { spn } = queryParams
    yield put(proSetSpn(spn))
    saveSpnInCookie(spn)
  }

  const isLogged = yield bootstrapAuthFromLs()
  yield put(setProLoggedIn(isLogged))

  if (isLogged) {
    const decodedToken = yield select(fromAuth.selectIdTokenDecoded)
    yield* decodeJwtToken(decodedToken)
    const pathName = yield select(fromRouting.getPathname)
    yield put(setPathNameOrigin(pathName))
  }
}

function* handleNewPasswordRequest({ email }) {
  try {
    yield* handleRequest({
      requestActions: proForgotPassword,
      promise: call(client(PRO_API, true).post, '/forgot-password/', { email }),
    })
    yield put(push('/login'))
    yield* notify('pro.thank_you', 'pro.reset_password_email')
    yield delay(3000)
    yield put(push('/login'))
  } catch (e) {
    if (e.response.status === 400) {
      // eslint-disable-next-line no-underscore-dangle
      yield put(
        stopSubmit('ProForgotPasswordForm', {
          _error: e.response.data.message,
        }),
      )
    }
  }
}

function* handleUpdatePasswordFailure() {
  yield* notify('', 'firm.save.failure.message', 'error')
}

function* handleUpdatePasswordSuccess({ actionParams }) {
  yield put(push(actionParams.redirectURL))
  yield* notify('pro.thank_you', 'pro.reset_password_success')
}

function* handleUpdatePasswordRequest({ token = null, password, formType }) {
  let updatePasswordURL = '/change-password-user'
  let redirectURL =
    formType === NEW_FIRM_USER ? '/reset-password-confirmation' : '/login'

  if (token === null) {
    redirectURL = '/my-account/account/change-password'
  } else {
    updatePasswordURL += `/${token}`
  }

  yield* handleRequest({
    requestActions: proResetPassword,
    promise: call(client(PRO_API, true).post, updatePasswordURL, { password }),
    actionParams: {
      redirectURL,
    },
  })
}

function* handleProRegistrationRequest({
  cguDataPolicy,
  qualityCharter,
  ...formData
}) {
  let dataRegistration = formData
  if (!cguDataPolicy || !qualityCharter) {
    yield put(proRegistration.failure())
    return
  }

  if (!formData.lng || !formData.lat) {
    const place = yield geoCode(formData.localisation)
    const lng = place?.geometry?.location?.lng().toString()
    const lat = place?.geometry?.location?.lat().toString()

    dataRegistration = {
      ...formData,
      lng,
      lat,
    }
  }

  const countryCode = yield select(fromContext.getCountry)
  const url = `${urlPrefix}/firms/signup`

  const userData = {
    email: dataRegistration.email,
    firstName: dataRegistration.firstname,
    lastName: dataRegistration.lastname,
    gender: dataRegistration.gender,
    password: dataRegistration.password,
    mobilePhone: dataRegistration.phone,
  }

  const queryParams = yield select(fromContext.getInitialQueryParams)

  const data = {
    firm: {
      countryCode,
      name: dataRegistration.name,
      city: dataRegistration.city,
      postalCode: dataRegistration.postalCode,
      registrationNumber: dataRegistration.registrationNumber,
      point: {
        lng: dataRegistration.lng,
        lat: dataRegistration.lat,
      },
      trades: dataRegistration.trade,
      origin: queryParams.origin,
    },
    user: userData,
    prefix: dataRegistration.prefix,
  }

  const spn = yield select(fromPro.getSpn)
  if (spn) {
    data.spn = spn
  }

  yield* handleRequest({
    requestActions: proRegistration,
    promise: call(client(PRO_API, true).post, url, data),
    actionParams: {
      email: dataRegistration.email,
      password: dataRegistration.password,
      prefix: dataRegistration.prefix,
    },
  })
}

function* handleProRegistrationSuccess({ actionParams }) {
  const language = yield select(fromContext.getLang)
  const confirmationPath =
    CONFIRMATION_PATHS_FROM_PREFIX[actionParams.prefix] ||
    getFreeniumSignUpConfirmationPath

  yield wait(500)
  yield put(
    proAuthLogin.request({
      redirectPathname: confirmationPath(language),
      ...actionParams,
    }),
  )
}

function* handleProRegistrationFailure({ error }) {
  if (
    !error.response ||
    error.response.status < 400 ||
    error.response.status >= 500
  ) {
    yield put(setProFormRegistrationStep(1))

    return yield* notify('', 'firm.save.failure.message', 'error')
  }

  const pathName = yield select(fromRouting.getPathname)

  if (pathName === URLS.ROUTES.PRO_PARTNER_REGISTRATION) {
    return yield* notify('', error.response.data.message, 'error')
  }

  const errors = getFormErrors(
    fromAPIV2(error.response.data.message, PRO_SIGNUP_API_FORM_MAPPING),
  )
  let isError = false
  PRO_REGISTRATION_STEP_ONE.forEach(field => {
    if (field in errors) {
      isError = true
    }
  })

  // change step to see error message
  if (isError) {
    yield put(setProFormRegistrationStep(1))
  }

  yield put(stopSubmit(PRO_REGISTRATION_FORM_NAME, errors))
}

function* handleGetRegistrationNumberDataRequest({ searchRequest }, prefix) {
  if (!searchRequest) {
    return yield put(
      registrationNumberData.success({
        payload: [],
      }),
    )
  }

  yield pushGtmEvent({
    event: 'siret_search',
    category: PREFIX_CATEGORIES[prefix],
  })

  return yield* handleRequest({
    requestActions: registrationNumberData,
    promise: call(
      client(PRO_API, true).get,
      `/api/registration-number-data/${searchRequest}?limit=10`,
    ),
  })
}

function* handleGetRegistrationNumberDataSuccess({ payload }) {
  return yield put(setRegistrationNumberData(payload))
}

function* handleGetPrefilledRegistration({ partner, uuid }) {
  yield* handleRequest({
    requestActions: getPrefilledRegistration,
    promise: call(
      client(PRO_API, true).get,
      `api/prefilled_registrations/${uuid}`,
    ),
  })
}

function* redirectPartnerRegistration() {
  yield pushGtmEvent({
    event: 'redirect_partner_registration',
    category: 'Directory',
  })
  yield put(push(URLS.ROUTES.PRO_REGISTRATION))
}

function* handleLocationChange({
  payload: {
    location: { pathname },
  },
}) {
  if (pathname !== URLS.ROUTES.PRO_PARTNER_REGISTRATION) {
    return
  }

  const searchQuery = yield select(fromRouting.getQuery)

  if (!searchQuery || !searchQuery.uuid) {
    yield* redirectPartnerRegistration()
  }
}

function* handleGetPrefilledRegistrationFailure() {
  yield* redirectPartnerRegistration()
}

export default function* () {
  yield all([
    takeLatest(CLIENT_INITIATED, handleClientInitiated),
    takeLatest(LOCATION_CHANGE, handleLocationChange),
    takeEvery(PRO_CREATE.REQUEST, handleProCreateRequest),
    takeLatest(PRO_AUTH_LOGIN.REQUEST, handleProLoginRequest),
    takeLatest(PRO_AUTH_LOGOUT, handleProAuthLogout),
    takeLatest(PRO_UPDATE_PASSWORD.REQUEST, handleUpdatePasswordRequest),
    takeLatest(PRO_UPDATE_PASSWORD.FAILURE, handleUpdatePasswordFailure),
    takeLatest(PRO_UPDATE_PASSWORD.SUCCESS, handleUpdatePasswordSuccess),
    takeLatest(PRO_FORGOT_PASSWORD.REQUEST, handleNewPasswordRequest),
    takeLatest(
      REGISTRATION_NUMBER_DATA.REQUEST,
      handleGetRegistrationNumberDataRequest,
    ),
    takeLatest(
      REGISTRATION_NUMBER_DATA.SUCCESS,
      handleGetRegistrationNumberDataSuccess,
    ),
    takeLatest(PRO_REGISTRATION.REQUEST, handleProRegistrationRequest),
    takeLatest(PRO_REGISTRATION.SUCCESS, handleProRegistrationSuccess),
    takeLatest(PRO_REGISTRATION.FAILURE, handleProRegistrationFailure),
    takeLatest(
      GET_PREFILLED_REGISTRATION.REQUEST,
      handleGetPrefilledRegistration,
    ),
    takeLatest(
      GET_PREFILLED_REGISTRATION.FAILURE,
      handleGetPrefilledRegistrationFailure,
    ),
  ])
}
